import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { DoorOpen, Package, Wrench, TrendingUp } from 'lucide-react';
import { toast } from 'react-toastify';
import { CardStats, ConfirmModal } from '@components/ui';
import MaterialPageLayout from '../../components/MaterialPageLayout';
import MaterialDetailModal from '../../components/MaterialDetailModal';
import { formatCurrency } from '../../components/StockIndicator';
import GardropTable from './GardropTable';
import GardropModal from './GardropModal';
import { getGardropByInsaat, getGardropStats } from '@mocks/materials/gardrop';
import OdemePlanMiniModal from '@components/shared/OdemePlanMiniModal';
import { addMockCariHareket } from '@mocks/cariHesaplar';

export default function Gardrop() {
    const { selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);
    const [filterTur, setFilterTur] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isOdemePlanOpen, setIsOdemePlanOpen] = useState(false);
    const [odemePlanData, setOdemePlanData] = useState(null);

    const insaatId = showAllConstructions ? null : selectedConstruction?.id;
    const filteredData = useMemo(() => {
        let data = getGardropByInsaat(insaatId);
        if (filterTur) data = data.filter(item => item.tur === filterTur);
        return data;
    }, [insaatId, filterTur]);
    const stats = useMemo(() => getGardropStats(insaatId), [insaatId]);

    const handleAdd = () => { setSelectedItem(null); setIsModalOpen(true); };
    const handleView = (item) => { setSelectedItem(item); setIsDetailOpen(true); };
    const handleEdit = (item) => { setSelectedItem(item); setIsModalOpen(true); };
    const handleDelete = (item) => { setSelectedItem(item); setIsDeleteOpen(true); };
    const handleSubmit = async (fd, isEdit) => {
        setIsLoading(true); await new Promise(r => setTimeout(r, 800));
        if (!isEdit && fd.supplierId && fd.kdvDahilToplam > 0) {
            addMockCariHareket(fd.supplierId, insaatId || 'c1', { type: 'purchase', subtype: 'fatura', amount: fd.kdvDahilToplam, date: fd.siparisTarihi || new Date().toISOString().split('T')[0], description: fd.autoDescription || `Gardröp — ${fd.miktar} adet ${fd.tur}` });
        }
        toast.success(isEdit ? 'Kayıt güncellendi' : 'Yeni kayıt oluşturuldu');
        setIsLoading(false); setIsModalOpen(false); setSelectedItem(null);
        if (!isEdit && fd.supplierId && fd.kdvDahilToplam > 0) { setOdemePlanData({ amount: fd.kdvDahilToplam, companyName: fd.tedarikci }); setIsOdemePlanOpen(true); }
    };
    const handleConfirmDelete = async () => { setIsLoading(true); await new Promise(r => setTimeout(r, 500)); toast.success('Kayıt silindi'); setIsLoading(false); setIsDeleteOpen(false); setSelectedItem(null); };
    const handleOdemePlanConfirm = ({ nextDueDate }) => { toast.success(`Ödeme planı oluşturuldu — Vade: ${nextDueDate}`); setIsOdemePlanOpen(false); setOdemePlanData(null); };

    const statsCards = [
        { title: 'Toplam Kalem', value: `${stats.toplamKalem}`, icon: Package, color: 'primary' },
        { title: 'Teslim Edilen', value: `${stats.teslimEdilen}`, icon: DoorOpen, color: 'success' },
        { title: 'Montajlanan', value: `${stats.montajlanan}`, icon: Wrench, color: 'info' },
        { title: 'Toplam Maliyet', value: formatCurrency(stats.toplamMaliyet), icon: TrendingUp, color: 'warning' },
    ];

    const detailFields = [
        { key: 'siparisTarihi', label: 'Sipariş Tarihi', type: 'date' }, { key: 'teslimTarihi', label: 'Teslim Tarihi', type: 'date' },
        { key: 'tedarikci', label: 'Tedarikçi' }, { key: 'tur', label: 'Tür' }, { key: 'model', label: 'Model' }, { key: 'renk', label: 'Renk' },
        { key: 'olcu', label: 'Ölçü' }, { key: 'malzeme', label: 'Malzeme' },
        { key: 'miktar', label: 'Miktar', type: 'number' }, { key: 'toplamFiyat', label: 'Toplam Tutar', type: 'currency' },
        { key: 'durum', label: 'Durum', type: 'status' }, { key: 'blokAdi', label: 'Blok' },
    ];

    const headerFilter = (
        <select value={filterTur} onChange={(e) => setFilterTur(e.target.value)}
            className="px-3 py-2 bg-bg-primary border border-border-light rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/50 min-w-[160px]">
            <option value="">Tüm Türler</option>
            <option value="Yatak Odası Gardırop">Yatak Odası Gardırop</option>
            <option value="Antre Dolabı">Antre Dolabı</option>
            <option value="Giyinme Odası">Giyinme Odası</option>
        </select>
    );

    return (
        <>
            <MaterialPageLayout title="Gardırop & Dolaplar" subtitle="İç Mekan & Mobilya" icon={DoorOpen}
                iconBgColor="bg-amber-600" backLink="/insaat/icmekan" stats={statsCards}
                onAddClick={handleAdd} headerFilter={headerFilter}>
                <GardropTable data={filteredData} onView={handleView} onEdit={handleEdit} onDelete={handleDelete} />
            </MaterialPageLayout>
            <GardropModal isOpen={isModalOpen} onClose={() => { setIsModalOpen(false); setSelectedItem(null); }}
                onSubmit={handleSubmit} initialData={selectedItem} isLoading={isLoading} insaatId={insaatId} />
            <MaterialDetailModal isOpen={isDetailOpen} onClose={() => { setIsDetailOpen(false); setSelectedItem(null); }}
                data={selectedItem} fields={detailFields} title="Gardırop / Dolap Detayı" />
            <ConfirmModal isOpen={isDeleteOpen} onClose={() => { setIsDeleteOpen(false); setSelectedItem(null); }}
                onConfirm={handleConfirmDelete} title="Kaydı Sil"
                message={`"${selectedItem?.tedarikci}" - ${selectedItem?.tur} kaydını silmek istediğinize emin misiniz?`}
                confirmText="Evet, Sil" isLoading={isLoading} />
            <OdemePlanMiniModal isOpen={isOdemePlanOpen} onClose={() => { setIsOdemePlanOpen(false); setOdemePlanData(null); }} onConfirm={handleOdemePlanConfirm} amount={odemePlanData?.amount} companyName={odemePlanData?.companyName} />
        </>
    );
}
